import type { Map as MapboxMap, ExpressionSpecification } from 'mapbox-gl';
import type { StationData } from '../../api/stationObservationsService';

/**
 * Wind speed tiers with corresponding colors, arrow sizes, and tail lengths.
 * Wind speed values are in km/h.
 * Colors match the FIA wind speed legend scale.
 * size: overall icon scale; tailLength: y-coordinate where tail ends (higher = longer tail).
 */
export const WIND_SPEED_TIERS = [
  { id: 0, label: '< 5 km/h',    maxKmh: 5,        color: '#22c55e', size: 0.7, tailLength: 55 },
  { id: 1, label: '5-10 km/h',   maxKmh: 10,       color: '#a3b817', size: 0.8, tailLength: 80 },
  { id: 2, label: '10-15 km/h',  maxKmh: 15,      color: '#f59e0b', size: 0.9, tailLength: 105 },
  { id: 3, label: '15-20 km/h',  maxKmh: 20,      color: '#ea580c', size: 1.0, tailLength: 130 },
  { id: 4, label: '> 20 km/h',   maxKmh: Infinity,  color: '#dc2626', size: 1.1, tailLength: 150 },
] as const;

/** Default color used when wind speed is unknown */
const DEFAULT_COLOR = '#6b7280';
/** Default size used when wind speed is unknown */
const DEFAULT_SIZE = 0.7;
/** Default tail length for unknown speed (same as lowest tier) */
const DEFAULT_TAIL_LENGTH = 55;

/**
 * Get the wind speed tier index for a given speed in km/h.
 * Returns the tier index (0-4), or -1 if speed is null/undefined.
 */
export function getWindSpeedTierIndex(speedKmh: number | null | undefined): number {
  if (speedKmh === null || speedKmh === undefined) return -1;
  for (let i = 0; i < WIND_SPEED_TIERS.length; i++) {
    if (speedKmh < WIND_SPEED_TIERS[i].maxKmh) return i;
  }
  return WIND_SPEED_TIERS.length - 1;
}

/** Base width for the arrow SVG */
const ARROW_WIDTH = 60;

/** Padding below tail so anchor (bottom) sits at station with small clearance */
const TAIL_TO_ANCHOR_PADDING = 10;

/**
 * Create a high-resolution SVG arrow string for a given fill color and tail length.
 * The arrow points up (north) and will be rotated by the map layer based on wind direction.
 * Simple triangle head + rectangular tail for clean rendering.
 * Height is dynamic: tailLength + padding, so icon-anchor 'bottom' places the tail
 * near the station without icon-offset (fixes zoom drift) and keeps arrows close.
 */
function createArrowSvg(fillColor: string, tailLength: number): string {
  const height = tailLength + TAIL_TO_ANCHOR_PADDING;
  // Narrower arrow: tail 26-34 (8px), head base 18-42 (24px), stroke 2
  const path = `M 30 12 L 42 40 L 34 40 L 34 ${tailLength} L 26 ${tailLength} L 26 40 L 18 40 Z`;
  return `<svg width="${ARROW_WIDTH}" height="${height}" viewBox="0 0 ${ARROW_WIDTH} ${height}" xmlns="http://www.w3.org/2000/svg"><path d="${path}" fill="${fillColor}" stroke="#ffffff" stroke-width="2" stroke-linejoin="round" stroke-linecap="round"/></svg>`;
}

/**
 * Get the image name for a wind arrow tier, with an optional prefix for PiP/split maps.
 */
function getArrowImageName(tierIndex: number, prefix: string): string {
  if (tierIndex < 0) return `${prefix}wind-arrow-default`;
  return `${prefix}wind-arrow-${tierIndex}`;
}

/**
 * Add all wind arrow images (one per tier + default) to a Mapbox map.
 * Returns a Promise that resolves when all images have been added.
 * @param map - The Mapbox map instance
 * @param prefix - A prefix for image names (e.g., '' for main, 'pip-' for PiP, 'split-' for split)
 */
export function addWindArrowImages(map: MapboxMap, prefix: string = ''): Promise<void> {
  const imageNames = WIND_SPEED_TIERS.map((_, i) => getArrowImageName(i, prefix));
  imageNames.push(getArrowImageName(-1, prefix)); // default image

  const colors = [...WIND_SPEED_TIERS.map(t => t.color), DEFAULT_COLOR];
  const tailLengths = [...WIND_SPEED_TIERS.map(t => t.tailLength), DEFAULT_TAIL_LENGTH];

  // Check which images still need to be added
  const missingImages: { name: string; color: string; tailLength: number }[] = [];
  for (let i = 0; i < imageNames.length; i++) {
    if (!map.hasImage(imageNames[i])) {
      missingImages.push({ name: imageNames[i], color: colors[i], tailLength: tailLengths[i] });
    }
  }

  if (missingImages.length === 0) return Promise.resolve();

  return new Promise((resolve) => {
    let loaded = 0;
    const total = missingImages.length;

    for (const { name, color, tailLength } of missingImages) {
      const svg = createArrowSvg(color, tailLength);
      const height = tailLength + TAIL_TO_ANCHOR_PADDING;
      const img = new Image(ARROW_WIDTH, height);
      img.onload = () => {
        if (!map.hasImage(name)) {
          map.addImage(name, img);
        }
        loaded++;
        if (loaded >= total) resolve();
      };
      img.onerror = () => {
        loaded++;
        if (loaded >= total) resolve();
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(svg);
    }
  });
}

/**
 * Check if all wind arrow images are loaded on the map.
 */
export function hasAllWindArrowImages(map: MapboxMap, prefix: string = ''): boolean {
  for (let i = 0; i < WIND_SPEED_TIERS.length; i++) {
    if (!map.hasImage(getArrowImageName(i, prefix))) return false;
  }
  if (!map.hasImage(getArrowImageName(-1, prefix))) return false;
  return true;
}

/**
 * Get the wind speed in km/h from a station observation.
 * Wind speed is stored in m/s in the readings array; this converts to km/h.
 * @param stationData - The station data containing reading_config and observations
 * @param observation - The specific observation to read from
 * @returns Wind speed in km/h, or null if not available
 */
export function getWindSpeedKmh(
  stationData: StationData,
  observation: { readings: (number | null)[] }
): number | null {
  // Find the windSpeed index in reading_config (field stored as camelCase 'windSpeed')
  const windSpeedIndex = stationData.reading_config.findIndex(r => r.name === 'windSpeed');
  if (windSpeedIndex === -1) return null;

  const speedMs = observation.readings[windSpeedIndex];
  if (speedMs === null || speedMs === undefined) return null;

  // Convert m/s to km/h
  return speedMs * 3.6;
}

/**
 * Get the wind gust in km/h from a station observation.
 * Wind gust is stored in m/s in the readings array; this converts to km/h.
 * @param stationData - The station data containing reading_config and observations
 * @param observation - The specific observation to read from
 * @returns Wind gust in km/h, or null if not available
 */
export function getWindGustKmh(
  stationData: StationData,
  observation: { readings: (number | null)[] }
): number | null {
  // Find the windGust index in reading_config (field stored as camelCase 'windGust')
  const windGustIndex = stationData.reading_config.findIndex(r => r.name === 'windGust');
  if (windGustIndex === -1) return null;

  const gustMs = observation.readings[windGustIndex];
  if (gustMs === null || gustMs === undefined) return null;

  // Convert m/s to km/h
  return gustMs * 3.6;
}

/**
 * Get the raw geographic wind direction (degrees from north, clockwise) from a station observation.
 * This is the absolute wind direction, NOT the car-relative direction.
 * Returns the direction the wind is BLOWING TOWARDS (original + 180°), suitable for map display.
 * @param stationData - The station data containing reading_config and observations
 * @param observation - The specific observation to read from
 * @returns Wind direction in degrees (0-360) showing where wind blows to, or null if not available
 */
export function getGeographicWindDirection(
  stationData: StationData,
  observation: { readings: (number | null)[] }
): number | null {
  // Find the windDirection index in reading_config (field stored as camelCase 'windDirection')
  const windDirIndex = stationData.reading_config.findIndex(r => r.name === 'windDirection');
  if (windDirIndex === -1) return null;

  const dirDegrees = observation.readings[windDirIndex];
  if (dirDegrees === null || dirDegrees === undefined) return null;

  // Add 180° to convert from "coming from" to "blowing towards" convention
  return (dirDegrees + 180) % 360;
}

/**
 * Build Mapbox expression for data-driven icon-image based on windSpeedKmh property.
 * Falls back to default arrow when speed is unknown (tier = -1).
 */
export function buildIconImageExpression(prefix: string = ''): ExpressionSpecification {
  return [
    'step',
    ['get', 'windSpeedKmh'],
    getArrowImageName(0, prefix),    // < 5 km/h
    5, getArrowImageName(1, prefix),  // 5-10 km/h
    10, getArrowImageName(2, prefix), // 10-15 km/h
    15, getArrowImageName(3, prefix), // 15-20 km/h
    20, getArrowImageName(4, prefix), // > 20 km/h
  ] as ExpressionSpecification;
}

/**
 * Build Mapbox expression for data-driven icon-size based on windSpeedKmh property.
 */
export function buildIconSizeExpression(): ExpressionSpecification {
  return [
    'step',
    ['get', 'windSpeedKmh'],
    WIND_SPEED_TIERS[0].size,     // < 5 km/h
    5, WIND_SPEED_TIERS[1].size,  // 5-10 km/h
    10, WIND_SPEED_TIERS[2].size, // 10-15 km/h
    15, WIND_SPEED_TIERS[3].size, // 15-20 km/h
    20, WIND_SPEED_TIERS[4].size, // > 20 km/h
  ] as ExpressionSpecification;
}

/**
 * Get the icon-image value for a specific windSpeedKmh (used when windSpeedKmh is -1/unknown,
 * as the step expression won't handle negative values well).
 * For features with unknown speed, set a flag so we can use case expression.
 */
export function getDefaultArrowImageName(prefix: string = ''): string {
  return getArrowImageName(-1, prefix);
}

/**
 * Build full icon-image expression that handles both known and unknown wind speeds.
 * Features with unknown speed have windSpeedKmh set to -1.
 */
export function buildFullIconImageExpression(prefix: string = ''): ExpressionSpecification {
  return [
    'case',
    ['<', ['get', 'windSpeedKmh'], 0],
    getArrowImageName(-1, prefix), // Unknown speed -> default gray arrow
    buildIconImageExpression(prefix),
  ] as ExpressionSpecification;
}

/**
 * Build full icon-size expression that handles both known and unknown wind speeds.
 */
export function buildFullIconSizeExpression(): ExpressionSpecification {
  return [
    'case',
    ['<', ['get', 'windSpeedKmh'], 0],
    DEFAULT_SIZE, // Unknown speed -> default size
    buildIconSizeExpression(),
  ] as ExpressionSpecification;
}

/**
 * Zoom stops with scale factors for arrow sizing.
 * At each zoom level, all tier sizes are pre-multiplied by the zoom factor.
 */
const ZOOM_SCALE_STOPS: { zoom: number; factor: number }[] = [
  { zoom: 9, factor: 0.35 },
  { zoom: 11, factor: 0.5 },
  { zoom: 13, factor: 0.7 },
  { zoom: 15, factor: 0.85 },
  { zoom: 17, factor: 1.0 },
];

/**
 * Build a data-driven step expression for icon-size at a specific zoom factor.
 * Returns pre-multiplied sizes for each wind speed tier.
 */
function buildSizeStepAtZoom(factor: number): ExpressionSpecification {
  return [
    'case',
    ['<', ['get', 'windSpeedKmh'], 0],
    DEFAULT_SIZE * factor,
    [
      'step',
      ['get', 'windSpeedKmh'],
      WIND_SPEED_TIERS[0].size * factor,
      5, WIND_SPEED_TIERS[1].size * factor,
      10, WIND_SPEED_TIERS[2].size * factor,
      15, WIND_SPEED_TIERS[3].size * factor,
      20, WIND_SPEED_TIERS[4].size * factor,
    ],
  ] as ExpressionSpecification;
}

/**
 * Build composite icon-size expression: interpolate on zoom, data-driven at each stop.
 * Uses pre-multiplied values so no '*' operator is needed (avoids Mapbox rendering issues).
 */
export function buildFullIconSizeExpressionWithZoom(): ExpressionSpecification {
  // Build: ['interpolate', ['linear'], ['zoom'], z1, stepExpr1, z2, stepExpr2, ...]
  const expr: unknown[] = ['interpolate', ['linear'], ['zoom']];
  for (const { zoom, factor } of ZOOM_SCALE_STOPS) {
    expr.push(zoom, buildSizeStepAtZoom(factor));
  }
  return expr as ExpressionSpecification;
}

/**
 * Build Mapbox expression for the wind speed text label.
 * Shows rounded km/h value when known; appends wind gust when available.
 * Format: "12 km/h" or "12/25 km/h" when gust is present and > 0.
 */
export function buildTextFieldExpression(): ExpressionSpecification {
  return [
    'case',
    ['<', ['get', 'windSpeedKmh'], 0],
    '', // Unknown speed -> no label
    [
      'case',
      ['>', ['get', 'windGustKmh'], 0], // Only show gust when present and > 0
      [
        'concat',
        ['to-string', ['round', ['get', 'windSpeedKmh']]],
        '/',
        ['to-string', ['round', ['get', 'windGustKmh']]],
        ' km/h',
      ],
      [
        'concat',
        ['to-string', ['round', ['get', 'windSpeedKmh']]],
        ' km/h',
      ],
    ],
  ] as ExpressionSpecification;
}

/**
 * Build Mapbox expression for data-driven text color matching the arrow tier color.
 */
export function buildTextColorExpression(): ExpressionSpecification {
  return [
    'case',
    ['<', ['get', 'windSpeedKmh'], 0],
    DEFAULT_COLOR,
    [
      'step',
      ['get', 'windSpeedKmh'],
      WIND_SPEED_TIERS[0].color,     // < 5 km/h
      5, WIND_SPEED_TIERS[1].color,  // 5-10 km/h
      10, WIND_SPEED_TIERS[2].color, // 10-15 km/h
      15, WIND_SPEED_TIERS[3].color, // 15-20 km/h
      20, WIND_SPEED_TIERS[4].color, // > 20 km/h
    ],
  ] as ExpressionSpecification;
}
